/**
 * Socket.io subscriptions for host screens and team phones.
 *
 * The server sends only small "something changed" signals in these rooms
 * (see Cerebrex/utils/realtime.js). The caller reloads its data through
 * REST when a signal arrives, and also on every reconnect, because signals
 * sent while the socket was down are lost.
 */
import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import config from '../config';
import { socketAuth } from './authToken';

// Socket.io reads a path on the URL as a namespace. The API base can be
// "https://host/api", so connect to the origin. Same rule as HostPanel.
export function socketOrigin(): string {
  try {
    return new URL(config.apiBaseUrl).origin;
  } catch {
    return config.apiBaseUrl;
  }
}

type Handlers = Record<string, (payload: unknown) => void>;

/**
 * Join one room and call `handlers[event]` for each signal. `onReconnect`
 * runs after every reconnect (not the first connect).
 */
export function useRoomSignals(
  join: { event: 'join-host'; eventId: string } | { event: 'join-play'; pin: string } | null,
  handlers: Handlers,
  onReconnect?: () => void,
): void {
  const handlersRef = useRef(handlers);
  const reconnectRef = useRef(onReconnect);
  handlersRef.current = handlers;
  reconnectRef.current = onReconnect;

  const key = join ? `${join.event}:${'eventId' in join ? join.eventId : join.pin}` : '';

  useEffect(() => {
    if (!join) return;
    // The host room is operator-only, so a host screen sends the sign-in
    // on the handshake. `auth` is a function, so every reconnect gets a
    // fresh token. Team phones have no session and send nothing.
    const socket: Socket = io(socketOrigin(), {
      transports: ['websocket', 'polling'],
      ...(join.event === 'join-host' ? { auth: socketAuth } : {}),
    });
    let connectedOnce = false;
    socket.on('connect', () => {
      if ('eventId' in join) socket.emit('join-host', { eventId: join.eventId });
      else socket.emit('join-play', { pin: join.pin });
      if (connectedOnce) reconnectRef.current?.();
      connectedOnce = true;
    });
    const names = Object.keys(handlersRef.current);
    for (const name of names) {
      socket.on(name, (payload: unknown) => handlersRef.current[name]?.(payload));
    }
    return () => {
      socket.disconnect();
    };
    // `key` captures the room identity. Handlers are read through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
